/**
 * Reimbursement Internal Note — Add / Edit modal.
 * Angular parity with `FrequentlyUsedNotesConfigEditComponent`:
 *   - single `note_text` field (required)
 *   - hashtag normalization before submit (`#   Foo` → `#Foo`)
 *   - guard: "Note text cannot be empty." when normalized text is blank
 *   - Create posts `{ note_text }`; Update PUTs `{ note_text }`.
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import modalStyles from '@/features/application/styles/modals.module.css'
import { cn } from '@/lib/utils/helpers'
import { reimbursementNoteFormSchema, type ReimbursementNoteFormValues } from '../types/schemas'
import type { ReimbursementNoteEditModalProps } from '../types'
import {
  createReimbursementNote,
  updateReimbursementNote,
} from '../services/reimbursementNotesService'
import { normalizeHashtagNote } from '../utils/businessConfigHelpers'

export function ReimbursementNoteEditModal({
  isOpen,
  target,
  onClose,
  onSaved,
}: ReimbursementNoteEditModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const isEdit = Boolean(target?.notes_check_list_id)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ReimbursementNoteFormValues>({
    resolver: zodResolver(reimbursementNoteFormSchema),
    mode: 'onChange',
    defaultValues: { note_text: '' },
  })

  useEffect(() => {
    if (!isOpen) return
    setFormError(null)
    reset({ note_text: target?.note_text ?? '' })
  }, [isOpen, target, reset])

  const onValid = async (values: ReimbursementNoteFormValues) => {
    const noteText = normalizeHashtagNote(values.note_text)
    if (!noteText) {
      setFormError('Note text cannot be empty.')
      return
    }
    setFormError(null)
    setSubmitting(true)
    try {
      if (target) {
        await updateReimbursementNote(target.notes_check_list_id, { note_text: noteText })
        showSuccess('Note updated successfully.')
      } else {
        await createReimbursementNote({ note_text: noteText })
        showSuccess('Note created successfully.')
      }
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const title = isEdit ? 'Edit Message' : 'Add Message'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-2xl">
      <form onSubmit={handleSubmit(onValid)} className="min-w-0 space-y-4 p-2 sm:p-4 md:p-6">
        {formError && (
          <p className={modalStyles.formFieldError} role="alert">
            {formError}
          </p>
        )}

        <div>
          <label htmlFor="reimbursement-note-text" className="block text-sm font-medium text-slate-700 mb-1">
            Note Text
          </label>
          <textarea
            id="reimbursement-note-text"
            rows={4}
            placeholder="Enter note text"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register('note_text')}
          />
          {errors.note_text && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.note_text.message}
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isValid || submitting}
            className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'w-full sm:w-auto')}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
